import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Button from '../../../components/v2/buttons/Button'
import PaymentCard from '../../../components/v2/cards/PaymentCard'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
//
//
const PendingPayments: React.FC<{
  orderInfo?: any
}> = ({ orderInfo }) => {
  //
  const { isProduction } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  // selected payment method
  const [selectedPaymentMethodState, setSelectedPaymentMethodState] = useState<string | undefined>(undefined)
  //
  const [subscriptionAgree, setSubscriptionAgree] = useState<boolean>(false)
  //
  const [getPaymentUrl, { data: paymentUrl, isLoading: isPaymentLoading }] = api.useLazyGetTravelPolicyPaymentUrlQuery()
  //
  const [getPetPaymentUrl, { data: petPaymentResponse, isLoading: isPetPaymentUrlLoading }] =
    api.usePetPaymentMutation()
  //
  const [getTplPaymentUrl, { data: tplPaymentResponse, isLoading: isTplPaymentUrlLoading }] =
    api.useTplPaymentMutation()
  //
  // After Payment Steps On DEV for welcomer and travel
  const [afterPaymentStepsOnDev, { isLoading: isAfterPaymentStepsOnDevLoading }] =
    api.useAfterPaymentStepsOnDevMutation()
  //
  // After Payment Steps On DEV for pet
  const [afterPaymentStepsOnDevPet, { isLoading: isAfterPaymentStepsOnDevPetLoading }] =
    api.useAfterPetPaymentStepsOnDevMutation()
  //
  // After Payment Steps On DEV for tpl
  const [afterPaymentStepsOnDevTpl, { isLoading: isAfterPaymentStepsOnDevTplLoading }] =
    api.useAfterTplPaymentStepsOnDevMutation()
  //
  //
  //
  //
  // look order id and payment method
  const _getAbroadPaymentUrlCallback = useCallback(async () => {
    //
    if (orderInfo.orderId && selectedPaymentMethodState && isProduction) {
      try {
        await getPaymentUrl({
          orderId: orderInfo.orderId,
          paymentType:
            selectedPaymentMethodState === 'master' && !orderInfo?.discountUsed ? selectedPaymentMethodState : 'other',
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} AbroadPayment.tsx`)
        }
      }
    }
  }, [orderInfo, getPaymentUrl, selectedPaymentMethodState, t, isProduction])
  //
  const _getPetPaymentUrlCallback = useCallback(async () => {
    //
    if (
      orderInfo.orderId &&
      selectedPaymentMethodState &&
      isProduction &&
      (orderInfo?.paymentScheduleType?.includes('MONTHLY') ||
      orderInfo?.paymentScheduleType?.includes('MONTHLY-FIRST-TWO')
        ? subscriptionAgree
        : true)
    ) {
      try {
        await getPetPaymentUrl({
          orderId: orderInfo.orderId,
          paymentType: 'other',
          recurrentPaymentApprove: subscriptionAgree,
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} PetPayment.tsx`)
        }
      }
    }
  }, [orderInfo, getPetPaymentUrl, selectedPaymentMethodState, t, isProduction, subscriptionAgree])
  //
  const _getTplPaymentUrlCallback = useCallback(async () => {
    //
    if (
      orderInfo.orderId &&
      selectedPaymentMethodState &&
      isProduction &&
      (orderInfo?.paymentScheduleType?.includes('MONTHLY') ||
      orderInfo?.paymentScheduleType?.includes('MONTHLY-FIRST-TWO')
        ? subscriptionAgree
        : true)
    ) {
      try {
        await getTplPaymentUrl({
          orderId: orderInfo.orderId,
          paymentType: 'other',
          recurrentPaymentApprove: subscriptionAgree,
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} TplPayment.tsx`)
        }
      }
    }
  }, [orderInfo, getTplPaymentUrl, selectedPaymentMethodState, t, isProduction, subscriptionAgree])
  //
  const _getInternationalTplPaymentUrlCallback = useCallback(async () => {
    //
    if (orderInfo.orderId && selectedPaymentMethodState && isProduction) {
      try {
        await getPaymentUrl({
          orderId: orderInfo.orderId,
          paymentType: 'other',
        }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} InternationalTplPayment.tsx`)
        }
      }
    }
  }, [orderInfo, getPaymentUrl, selectedPaymentMethodState, t, isProduction])
  //
  const _getWelcomerPaymentUrlCallback = useCallback(async () => {
    //
    if (orderInfo.orderId && selectedPaymentMethodState && isProduction) {
      try {
        await getPaymentUrl({ orderId: orderInfo.orderId, paymentType: 'other' }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} WelcomerPayment.tsx`)
        }
      }
    }
  }, [orderInfo, getPaymentUrl, selectedPaymentMethodState, t, isProduction])
  //
  //
  const isTplCategory = () => orderInfo?.category?.includes('tpl')
  const isInternationalTplCategory = () => orderInfo?.category?.includes('international-tpl')

  const _rootPaymentUrl = () => {
    if (orderInfo.category.includes('travel-outside-georgia')) {
      _getAbroadPaymentUrlCallback()
    } else if (isTplCategory()) {
      _getTplPaymentUrlCallback()
    } else if (isInternationalTplCategory()) {
      _getInternationalTplPaymentUrlCallback()
    } else if (orderInfo.category.includes('welcomer')) {
      _getWelcomerPaymentUrlCallback()
    } else if (orderInfo.category.includes('pet')) {
      _getPetPaymentUrlCallback()
    }
  }
  //
  // main payment for welcomer and travel
  const travelAndWelcomerMainPayment = useCallback(() => {
    if (orderInfo.orderId && isProduction) {
      // got to checkout page
      if (paymentUrl && paymentUrl.url && isProduction) {
        //
        document.location.href = paymentUrl.url
      }
    }
  }, [isProduction, orderInfo.orderId, paymentUrl])
  //
  useEffect(() => {
    travelAndWelcomerMainPayment()
    return () => {}
  }, [travelAndWelcomerMainPayment])

  // main payment for pet
  const mainPaymentPet = useCallback(() => {
    if (orderInfo.orderId && isProduction) {
      // got to checkout page
      if (petPaymentResponse && petPaymentResponse.paymentGatewayUrl) {
        //
        document.location.href = petPaymentResponse.paymentGatewayUrl
        //
      }
    }
  }, [isProduction, orderInfo.orderId, petPaymentResponse])
  //
  useEffect(() => {
    mainPaymentPet()
    return () => {}
  }, [mainPaymentPet])

  // main payment for tpl
  const mainPaymentTpl = useCallback(() => {
    if (orderInfo.orderId && isProduction) {
      // got to checkout page
      if (tplPaymentResponse && tplPaymentResponse.paymentGatewayUrl) {
        //
        document.location.href = tplPaymentResponse.paymentGatewayUrl
        //
      }
    }
  }, [isProduction, orderInfo.orderId, tplPaymentResponse])
  //
  useEffect(() => {
    mainPaymentTpl()
    return () => {}
  }, [mainPaymentTpl])
  //
  // fake payment for travel and welcomer
  const travelAndWelcomerFakePayment = async () => {
    if (orderInfo?.orderId && !isProduction && orderInfo?.totalPrice !== null) {
      //
      try {
        //
        await afterPaymentStepsOnDev({
          orderId: orderInfo?.orderId,
          amount: orderInfo?.totalPrice,
        }).unwrap()
        //
        toast.success(`${t('paymentSuccessfully')}`)
        navigate('/pay/payment-success')
        //ugly page refresh
        window.location.reload()
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
          //
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, PendingPayments.tsx')
        }
      }
      //
    }
  }
  //
  // fake payment for tpl
  const fakePaymentTpl = async () => {
    if (orderInfo?.orderId && !isProduction && orderInfo?.totalPrice !== null) {
      //
      try {
        //
        await afterPaymentStepsOnDevTpl({
          orderId: orderInfo?.orderId,
          amount: orderInfo?.totalPrice,
        }).unwrap()
        //
        toast.success(`${t('paymentSuccessfully')}`)
        //
        navigate('/pay/payment-success')
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
          //
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, Payment.tsx')
        }
      }
      //
    }
  }
  //
  //
  // fake payment for pet
  const fakePaymentPet = async () => {
    if (orderInfo?.orderId && !isProduction && orderInfo?.totalPrice !== null) {
      //
      try {
        //
        await afterPaymentStepsOnDevPet({
          orderId: orderInfo?.orderId,
          amount: orderInfo?.totalPrice,
        }).unwrap()
        //
        toast.success(`${t('paymentSuccessfully')}`)
        //
        navigate('/pay/payment-success')
        //
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
          //
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, Payment.tsx')
        }
      }
      //
    }
  }
  //
  // disable browser back button
  useEffect(() => {
    const disableBackButton = (e: any) => {
      e.preventDefault()
      window.history.forward()
    }

    window.history.pushState('', '', window.location.href)
    window.addEventListener('popstate', disableBackButton)

    return () => {
      window.removeEventListener('popstate', disableBackButton)
    }
  }, [])
  //
  //
  const MainLoading =
    isPaymentLoading ||
    isAfterPaymentStepsOnDevLoading ||
    isTplPaymentUrlLoading ||
    isPetPaymentUrlLoading ||
    isAfterPaymentStepsOnDevPetLoading ||
    isAfterPaymentStepsOnDevTplLoading
  //
  return MainLoading ? (
    <Loader text={t('redirectionToPayment')} />
  ) : (
    <Wrapper>
      <ScreenHeader />

      <Content>
        <Title text={t('chooseCard')} />

        <Cards>
          {['master', 'visa'].map((item, index) => {
            return (
              <PaymentCard
                key={index}
                cardName={item === 'master' ? 'Mastercard' : 'Visa'}
                selected={item === selectedPaymentMethodState}
                cardType={item}
                saleCount={
                  item === 'master' &&
                  orderInfo?.category.includes('travel-outside-georgia') &&
                  !orderInfo?.discountUsed
                    ? 50
                    : undefined
                }
                onClick={() => {
                  if (selectedPaymentMethodState === item) {
                    setSelectedPaymentMethodState(item)
                  } else {
                    setSelectedPaymentMethodState(item)
                    setSubscriptionAgree(false)
                  }
                }}
                subscriptionCheckbox={
                  item === selectedPaymentMethodState &&
                  (orderInfo?.paymentScheduleType?.includes('MONTHLY') ||
                    orderInfo?.paymentScheduleType?.includes('MONTHLY-FIRST-TWO'))
                }
                subscriptionValue={subscriptionAgree}
                onChecboxClick={() => setSubscriptionAgree(!subscriptionAgree)}
              />
            )
          })}

          {selectedPaymentMethodState !== undefined && orderInfo && orderInfo.orderId ? (
            <Button
              fullWidth={false}
              style={{
                marginTop: '48px',
              }}
              text={t('pay')}
              buttonType="primary"
              onClick={() => {
                //  get all product payment url
                if (orderInfo.orderId && isProduction) {
                  _rootPaymentUrl()
                }
                // fake payment for welcomer and travel
                if (
                  orderInfo?.orderId &&
                  orderInfo?.totalPrice !== null &&
                  !isProduction &&
                  (orderInfo?.category?.includes('travel-outside-georgia') || orderInfo?.category?.includes('welcomer'))
                ) {
                  travelAndWelcomerFakePayment()
                }
                // fake payment for tpl
                if (orderInfo?.orderId && orderInfo?.totalPrice !== null && !isProduction && isTplCategory()) {
                  fakePaymentTpl()
                }
                if (
                  orderInfo?.orderId &&
                  orderInfo?.totalPrice !== null &&
                  !isProduction &&
                  isInternationalTplCategory()
                ) {
                  travelAndWelcomerFakePayment()
                }
                // fake payment for pet
                if (
                  orderInfo?.orderId &&
                  orderInfo?.totalPrice !== null &&
                  !isProduction &&
                  orderInfo?.category?.includes('pet')
                ) {
                  fakePaymentPet()
                }
              }}
            />
          ) : null}
        </Cards>
        {/*  */}
      </Content>
    </Wrapper>
  )
}
//
// styles
const Cards = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-block: 32px;
`
const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
  border-radius: 4px;
  z-index: 2;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`
//
export default PendingPayments
